import React, { useState, useEffect } from 'react';
import { 
  Card, Typography, Space, Button, Table, Tag, Modal, Form, Input, Select, 
  DatePicker, Row, Col, Rate, Upload, Image, message, Popconfirm, Descriptions,
  Badge, Divider, Alert, Spin, Statistic, Switch, List, Avatar, InputNumber
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, StarOutlined, TrophyOutlined, 
  ShareAltOutlined, UploadOutlined, EyeOutlined, FileImageOutlined, SoundOutlined,
  VideoCameraOutlined, CloseOutlined, FilterOutlined, HistoryOutlined,
  CheckCircleOutlined, GiftOutlined, HeartOutlined, CrownOutlined, SmileOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import studentAPI from '../../services/studentAPI';
import api from '../../services/api';
import moment from 'moment';
import { downloadCsv, fetchAllPages } from '../../utils/csvExport';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const StudentPraise = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [praiseRecords, setPraiseRecords] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null); // 选中的班级ID
  const [filteredStudents, setFilteredStudents] = useState([]); // 根据班级筛选的学生
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);

  // 表扬类型选项
  const praiseTypes = [
    { value: 'excellent_performance', label: '学习优秀', color: 'blue' },
    { value: 'helpful_behavior', label: '助人为乐', color: 'green' },
    { value: 'leadership', label: '领导能力', color: 'purple' },
    { value: 'creativity', label: '创新思维', color: 'orange' },
    { value: 'teamwork', label: '团队合作', color: 'cyan' },
    { value: 'responsibility', label: '责任心强', color: 'geekblue' },
    { value: 'improvement', label: '进步显著', color: 'lime' },
    { value: 'attendance', label: '出勤优秀', color: 'gold' },
    { value: 'other', label: '其他', color: 'default' },
  ];

  // 表扬分类选项
  const categoryOptions = [
    { value: '学习表现', label: '学习表现', color: 'blue' },
    { value: '品德表现', label: '品德表现', color: 'green' },
    { value: '体育表现', label: '体育表现', color: 'orange' },
    { value: '艺术表现', label: '艺术表现', color: 'purple' },
    { value: '社会实践', label: '社会实践', color: 'cyan' },
    { value: '创新能力', label: '创新能力', color: 'magenta' },
  ];

  // 获取表扬记录列表
  const fetchPraiseRecords = async (params = {}) => {
    setLoading(true);
    try {
      const response = await studentAPI.praise.getPraise({
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...filters,
        ...params,
      });
      
      if (response.data.success) {
        setPraiseRecords(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
        }));
      }
    } catch (error) {
      message.error('获取表扬记录失败');
      console.error('获取表扬记录失败:', error);
      setPraiseRecords([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };


  // 获取班级列表
  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      if (response.data) {
        const classData = response.data.success ? response.data.data : (Array.isArray(response.data) ? response.data : []);
        setClasses(classData);
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
      setClasses([]);
    }
  };
  
  // 根据班级ID筛选学生（重构为按需从云端精准拉取该班级学生）
  const handleClassChange = async (classId) => {
    setSelectedClassId(classId);
    if (classId) {
      try {
        const response = await api.get('/students', {
          params: { classId }
        });
        if (response.data) {
          const studentData = response.data.success ? response.data.data : response.data;
          setFilteredStudents(Array.isArray(studentData) ? studentData : []);
        }
      } catch (error) {
        console.error('获取班级学生列表失败:', error);
        message.error('获取班级学生列表失败');
        setFilteredStudents([]);
      }
    } else {
      setFilteredStudents([]);
    }
    // 清空学生选择
    form.setFieldsValue({ student_id: undefined, student_name: undefined });
  };

  useEffect(() => {
    fetchPraiseRecords();
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchPraiseRecords();
  }, [pagination.current, pagination.pageSize, filters]);

  // 处理表格变化
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    setPagination(newPagination);
  };

  // 处理筛选
  const handleFilter = (values) => {
    setFilters(values);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 重置筛选
  const resetFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 打开新增/编辑模态框
  const openModal = (record = null) => {
    setEditingRecord(record);
    setModalVisible(true);
    setFileList([]);
    
    if (record) {
      form.setFieldsValue({
        ...record,
        praise_date: record.praise_date ? moment(record.praise_date) : null,
        is_public: record.is_public === 1,
      });
      // 如果是编辑，设置班级并从服务器按需拉取该班级学生
      if (record.class_id) {
        setSelectedClassId(record.class_id);
        api.get('/students', { params: { classId: record.class_id } }).then(response => {
          if (response.data) {
            const studentData = response.data.success ? response.data.data : response.data;
            const fetched = Array.isArray(studentData) ? studentData : [];
            const hasStudent = fetched.some(s => s.id === record.student_id);
            if (!hasStudent && record.student_id) {
              fetched.push({
                id: record.student_id,
                student_name: record.student_name,
                class_id: record.class_id
              });
            }
            setFilteredStudents(fetched);
          }
        }).catch(err => {
          console.error('获取编辑学生班级列表失败:', err);
          if (record.student_id) {
            setFilteredStudents([{
              id: record.student_id,
              student_name: record.student_name,
              class_id: record.class_id
            }]);
          }
        });
      }
    } else {
      form.resetFields();
      form.setFieldsValue({
        level: 3,
        is_public: true,
      });
      setSelectedClassId(null);
      setFilteredStudents([]);
    }
  };

  // 关闭模态框
  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    setFileList([]);
    setSelectedClassId(null);
    setFilteredStudents([]);
    form.resetFields();
  };

  // 查看详情
  const viewDetail = async (record) => {
    try {
      const response = await studentAPI.praise.getPraiseDetail(record.id);
      if (response.data.success) {
        setSelectedRecord(response.data.data);
        setDetailModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
      // 使用当前记录作为后备
      setSelectedRecord(record);
      setDetailModalVisible(true);
    }
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      const formData = {
        ...values,
        praise_date: values.praise_date ? values.praise_date.format('YYYY-MM-DD HH:mm:ss') : null,
        attachments: fileList,
        school_id: editingRecord?.school_id
          || filteredStudents.find(student => String(student.id) === String(values.student_id))?.school_id
          || classes.find(cls => String(cls.id) === String(values.class_id || selectedClassId))?.school_id
          || user.schoolId,
        is_public: values.is_public ? 1 : 0,
      };

      if (editingRecord) {
        await studentAPI.praise.updatePraise(editingRecord.id, formData);
        message.success('表扬记录更新成功');
      } else {
        await studentAPI.praise.createPraise(formData);
        message.success('表扬记录创建成功');
      }
      
      closeModal();
      fetchPraiseRecords();
    } catch (error) {
      message.error(editingRecord ? '更新失败' : '创建失败');
      console.error('提交失败:', error);
    }
  };

  // 删除记录
  const handleDelete = async (id) => {
    try {
      await studentAPI.praise.deletePraise(id);
      message.success('删除成功');
      fetchPraiseRecords();
    } catch (error) {
      message.error('删除失败');
      console.error('删除失败:', error);
    }
  };

  // 文件上传处理
  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // 预览文件
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  // 获取base64
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleExportRecords = async () => {
    try {
      message.loading({ content: '正在导出表扬记录...', key: 'export-praise', duration: 0 });
      const rows = await fetchAllPages(studentAPI.praise.getPraise, filters);
      if (rows.length === 0) {
        message.warning({ content: '没有可导出的表扬记录', key: 'export-praise' });
        return;
      }
      downloadCsv(`表扬记录-${moment().format('YYYYMMDD-HHmmss')}.csv`, [
        { title: '学生姓名', dataIndex: 'student_name' },
        { title: '班级', dataIndex: 'class_name' },
        { title: '表扬类型', render: (row) => praiseTypes.find((item) => item.value === row.praise_type)?.label || row.praise_type },
        { title: '分类', render: (row) => categoryOptions.find((item) => item.value === row.category)?.label || row.category },
        { title: '表扬标题', dataIndex: 'title' },
        { title: '等级', dataIndex: 'level' },
        { title: '奖励积分', dataIndex: 'points_awarded' },
        { title: '是否公开', render: (row) => row.is_public ? '公开' : '私密' },
        { title: '表扬时间', render: (row) => row.praise_date ? moment(row.praise_date).format('YYYY-MM-DD HH:mm:ss') : '' },
        { title: '描述', dataIndex: 'description' }
      ], rows);
      message.success({ content: `已导出 ${rows.length} 条表扬记录`, key: 'export-praise' });
    } catch (error) {
      console.error('导出表扬记录失败:', error);
      message.error({ content: '导出表扬记录失败', key: 'export-praise' });
    }
  };

  // 渲染附件
  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) {
      return <Text type="secondary">无附件</Text>;
    }

    return (
      <Space wrap>
        {attachments.map((attachment, index) => {
          const isImage = attachment.file_type === 'image';
          const isAudio = attachment.file_type === 'audio';
          const isVideo = attachment.file_type === 'video';

          return (
            <div key={index} style={{ display: 'inline-block', margin: '4px' }}>
              {isImage && (
                <Image
                  width={60}
                  height={60}
                  src={`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://127.0.0.1:4100')}${attachment.file_path}`}
                  style={{ objectFit: 'cover', borderRadius: '4px' }}
                  preview={{
                    src: `${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://127.0.0.1:4100')}${attachment.file_path}`,
                  }}
                />
              )}
              {isAudio && (
                <div style={{ 
                  width: 60, height: 60, display: 'flex', alignItems: 'center', 
                  justifyContent: 'center', background: '#f0f0f0', borderRadius: '4px' 
                }}>
                  <SoundOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                </div>
              )}
              {isVideo && (
                <div style={{ 
                  width: 60, height: 60, display: 'flex', alignItems: 'center', 
                  justifyContent: 'center', background: '#f0f0f0', borderRadius: '4px' 
                }}>
                  <VideoCameraOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                </div>
              )}
            </div>
          );
        })}
      </Space>
    );
  };

  // 渲染星级
  const renderStars = (level) => {
    return <Rate disabled value={level} style={{ fontSize: '14px' }} />;
  };

  // 获取表扬类型图标
  const getPraiseIcon = (type) => {
    const typeMap = {
      'excellent_performance': <TrophyOutlined />,
      'helpful_behavior': <HeartOutlined />,
      'leadership': <CrownOutlined />,
      'creativity': <GiftOutlined />,
      'teamwork': <SmileOutlined />,
      'responsibility': <CheckCircleOutlined />,
      'improvement': <StarOutlined />,
      'attendance': <CheckCircleOutlined />,
    };
    return typeMap[type] || <SmileOutlined />;
  };

  // 表格列定义
  const columns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100,
      fixed: 'left',
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 100,
    },
    {
      title: '表扬类型',
      dataIndex: 'praise_type',
      key: 'praise_type',
      width: 120,
      render: (type) => {
        const option = praiseTypes.find(item => item.value === type);
        return (
          <Tag color={option?.color} icon={getPraiseIcon(type)}>
            {option?.label || type}
          </Tag>
        );
      },
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category) => {
        const option = categoryOptions.find(item => item.value === category);
        return <Tag color={option?.color}>{option?.label || category}</Tag>;
      },
    },
    {
      title: '表扬标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 120,
      render: (level) => (
        <Space direction="vertical" size="small">
          {renderStars(level)}
          <Tag color="gold">{level}星表扬</Tag>
        </Space>
      ),
    },
    {
      title: '奖励积分',
      dataIndex: 'points_awarded',
      key: 'points_awarded',
      width: 100,
      render: (points) => points > 0 ? (
        <Tag color="green" icon={<StarOutlined />}>
          +{points}分
        </Tag>
      ) : '-',
    },
    {
      title: '公开',
      dataIndex: 'is_public',
      key: 'is_public',
      width: 80,
      render: (isPublic) => (
        <Tag color={isPublic ? 'green' : 'orange'}>
          {isPublic ? '公开' : '私密'}
        </Tag>
      ),
    },
    {
      title: '附件',
      dataIndex: 'attachment_count',
      key: 'attachment_count',
      width: 80,
      render: (count) => count > 0 ? <Badge count={count} size="small" /> : '-',
    },
    {
      title: '表扬时间',
      dataIndex: 'praise_date',
      key: 'praise_date',
      width: 120,
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => viewDetail(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条表扬记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 最近表扬列表
  const recentPraiseList = praiseRecords.slice(0, 3).map(item => ({
    title: `${item.student_name} - ${item.title}`,
    description: item.description,
    avatar: <Avatar style={{ backgroundColor: praiseTypes.find(p => p.value === item.praise_type)?.color || '#1890ff' }}>
      {item.student_name?.charAt(0)}
    </Avatar>,
    content: (
      <Space>
        <Tag color={praiseTypes.find(p => p.value === item.praise_type)?.color}>
          {praiseTypes.find(p => p.value === item.praise_type)?.label}
        </Tag>
        <Rate value={item.level} disabled size="small" />
      </Space>
    )
  }));

  return (
    <div>
      {/* 操作栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space wrap>
              <Input
                placeholder="搜索学生姓名"
                prefix={<SearchOutlined />}
                style={{ width: 200 }}
                allowClear
              />
              <Select
                placeholder="表扬类型"
                style={{ width: 120 }}
                allowClear
              >
                {praiseTypes.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="表扬分类"
                style={{ width: 120 }}
                allowClear
              >
                {categoryOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <RangePicker placeholder={['开始日期', '结束日期']} />
              <Button icon={<SearchOutlined />} onClick={fetchPraiseRecords}>
                搜索
              </Button>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal()}
            >
              新增表扬记录
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 表扬统计 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="本月表扬总数"
              value={praiseRecords.length}
              prefix={<StarOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
              suffix="次"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="五星表扬"
              value={praiseRecords.filter(p => p.level === 5).length}
              prefix={<TrophyOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
              suffix="次"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="表扬积分"
              value={praiseRecords.reduce((sum, p) => sum + (p.points_awarded || 0), 0)}
              prefix={<GiftOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
              suffix="分"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="公开表扬"
              value={praiseRecords.filter(p => p.is_public).length}
              prefix={<CrownOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
              suffix="次"
            />
          </Card>
        </Col>
      </Row>

      {/* 最近表扬和表扬类型统计 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card title="最近表扬" size="small">
            <List
              itemLayout="horizontal"
              dataSource={recentPraiseList}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={item.avatar}
                    title={item.title}
                    description={item.description}
                  />
                  {item.content}
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="表扬类型分布" size="small">
            <Alert
              message="表扬管理提醒"
              description="支持上传图片、音频、视频等多媒体附件作为表扬依据，激励学生积极向上。"
              type="success"
              showIcon
            />
          </Card>
        </Col>
      </Row>

      {/* 表扬记录表格 */}
      <Card
        title="表扬记录管理"
        extra={<Button icon={<HistoryOutlined />} onClick={handleExportRecords}>导出记录</Button>}
      >
        <Table
          columns={columns}
          dataSource={praiseRecords}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑表扬记录' : '新增表扬记录'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="班级"
                rules={[{ required: true, message: '请选择班级' }]}
              >
                <Select
                  placeholder="请先选择班级"
                  value={selectedClassId}
                  onChange={handleClassChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="student_id"
                label="学生"
                rules={[{ required: true, message: '请选择学生' }]}
              >
                <Select
                  placeholder={selectedClassId ? "选择学生" : "请先选择班级"}
                  disabled={!selectedClassId}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={(value) => {
                    const student = filteredStudents.find(s => s.id === value);
                    if (student) {
                      form.setFieldsValue({
                        student_name: student.student_name || student.name,
                        class_id: student.class_id,
                      });
                    }
                  }}
                >
                  {filteredStudents.map(student => (
                    <Option key={student.id} value={student.id}>
                      {student.student_name || student.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item name="student_name" label="学生姓名" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item name="class_id" label="班级ID" style={{ display: 'none' }}>
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="praise_type"
                label="表扬类型"
                rules={[{ required: true, message: '请选择表扬类型' }]}
              >
                <Select placeholder="选择表扬类型">
                  {praiseTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      <Tag color={type.color} icon={getPraiseIcon(type.value)}>
                        {type.label}
                      </Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="表扬分类"
                rules={[{ required: true, message: '请选择表扬分类' }]}
              >
                <Select placeholder="选择表扬分类">
                  {categoryOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      <Tag color={option.color}>{option.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="title"
            label="表扬标题"
            rules={[{ required: true, message: '请输入表扬标题' }]}
          >
            <Input placeholder="请输入表扬标题" />
          </Form.Item>

          <Form.Item
            name="description"
            label="表扬内容"
            rules={[{ required: true, message: '请输入表扬内容' }]}
          >
            <TextArea rows={4} placeholder="请详细描述表扬内容" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="level"
                label="表扬等级"
                rules={[{ required: true, message: '请选择表扬等级' }]}
              >
                <Rate />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="points_awarded" label="奖励积分">
                <InputNumber min={0} step={0.1} precision={1} style={{ width: '100%' }} placeholder="输入奖励积分" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="praise_date" label="表扬时间">
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  placeholder="选择表扬时间"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="is_public" label="公开表扬" valuePropName="checked">
            <Switch checkedChildren="公开" unCheckedChildren="私密" />
          </Form.Item>

          <Form.Item label="表扬依据附件">
            <Alert
              message="支持上传图片、音频、视频文件作为表扬依据"
              type="info"
              style={{ marginBottom: '16px' }}
            />
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleUploadChange}
              onPreview={handlePreview}
              beforeUpload={() => false} // 阻止自动上传
              accept="image/*,audio/*,video/*"
              multiple
            >
              {fileList.length >= 8 ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传附件</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRecord ? '更新' : '创建'}
              </Button>
              <Button onClick={closeModal}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="表扬记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedRecord && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="学生姓名">
                {selectedRecord.student_name}
              </Descriptions.Item>
              <Descriptions.Item label="班级">
                {selectedRecord.class_name}
              </Descriptions.Item>
              <Descriptions.Item label="表扬类型">
                {(() => {
                  const option = praiseTypes.find(item => item.value === selectedRecord.praise_type);
                  return <Tag color={option?.color} icon={getPraiseIcon(selectedRecord.praise_type)}>{option?.label}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="表扬分类">
                {(() => {
                  const option = categoryOptions.find(item => item.value === selectedRecord.category);
                  return <Tag color={option?.color}>{option?.label}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="表扬标题" span={2}>
                {selectedRecord.title}
              </Descriptions.Item>
              <Descriptions.Item label="表扬内容" span={2}>
                {selectedRecord.description}
              </Descriptions.Item>
              <Descriptions.Item label="表扬等级">
                {renderStars(selectedRecord.level)}
              </Descriptions.Item>
              <Descriptions.Item label="表扬时间">
                {moment(selectedRecord.praise_date).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="奖励积分">
                {selectedRecord.points_awarded > 0 ? (
                  <Text type="success">+{selectedRecord.points_awarded}</Text>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="公开表扬">
                {selectedRecord.is_public ? <Badge status="success" text="是" /> : <Badge status="default" text="否" />}
              </Descriptions.Item>
              <Descriptions.Item label="表扬教师">
                {selectedRecord.teacher_name}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {moment(selectedRecord.created_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
            </Descriptions>

            {selectedRecord.attachments && selectedRecord.attachments.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <Title level={5}>表扬依据附件</Title>
                <div style={{ marginTop: '16px' }}>
                  {renderAttachments(selectedRecord.attachments)}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 图片预览 */}
      <Modal
        open={previewVisible}
        title="图片预览"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default StudentPraise;
